import { useCallback } from "react";
import { useGlobalState } from "../global-state/global.state";
import { useTeacherApi } from "../../apis/teacher-api";
import { useSchoolApi } from "../../apis/school-api";
import { produce } from "immer";
import { useQueryClient } from "react-query";
import { AddItemAction } from "../../helpers/state-helpers/crud-actions";
import { genericCrudReducer } from "../../helpers/state-helpers/generic-crud-reducer";
import { Address } from "../../models/address";
import useCache from "../../hooks/useCache";
import { SortAlphabeticallyWithKey } from "../../helpers/helper-functions";

export function useTeachers() {
  const { setGlobalState, globalState, setIsLoading } = useGlobalState();
  const { getTeacher } = useTeacherApi();
  const { getTeachersOfSchool, addExternalTeacherToSchool } = useSchoolApi();
  const { invalidate } = useCache();

  const queryClient = useQueryClient();

  const getTeacherById = useCallback(
    async (id: string) => {
      let teacher = globalState.teachers.find((t) => t.email === id);
      if (teacher) {
        return teacher;
      }
      setIsLoading(true);
      try {
        teacher = await getTeacher(id);

        if (teacher) {
          setGlobalState({
            teachers: genericCrudReducer(
              globalState.teachers,
              new AddItemAction(teacher),
            ),
          });
        }
      } finally {
        setIsLoading(false);
      }

      return teacher;
    },
    [setGlobalState, globalState, setIsLoading, getTeacher],
  );

  const _getTeachersOfSchool = useCallback(
    async (schoolId: string, isExternal?: boolean) => {
      const teachers = await getTeachersOfSchool(schoolId).then((teachers) => {
        if (isExternal === true) {
          return teachers.filter((t) => !t.internal);
        } else if (isExternal === false) {
          return teachers.filter((t) => t.internal);
        }
        return teachers.sort(SortAlphabeticallyWithKey('fullName'));
      });

      setGlobalState({
        teachers: [
          ...globalState.teachers.filter(
            (stateTeacher) =>
              !teachers.some(
                (loadedTeacher) => stateTeacher.id === loadedTeacher.id,
              ),
          ),
          ...teachers,
        ],
      });

      return teachers;
    },
    [getTeachersOfSchool, globalState.teachers, setGlobalState],
  );

  const _addExternalTeacherToSchool = useCallback(
    async (schoolId: string, teacherEmail: string, details: Address) => {
      await addExternalTeacherToSchool(schoolId, teacherEmail, {
        ...details,
        lastName: details.lastname,
      });

      const teacher = globalState.teachers.find((t) => t.id === teacherEmail);
      if (teacher) {
        return teacher;
      } else {
        const teacher = await getTeacher(teacherEmail);
        setGlobalState(
          produce(globalState, (draft) => {
            draft.teachers.push(teacher);
          }),
        );
        await queryClient.cancelQueries(["teachers", schoolId]);
        queryClient.setQueryData(
          ["teachers", schoolId],
          [...globalState.teachers, teacher],
        );
        invalidate({ type: "teacher", contexts: [schoolId] });
        return teacher;
      }
    },
    [
      addExternalTeacherToSchool,
      getTeacher,
      globalState,
      queryClient,
      setGlobalState,
      invalidate,
    ],
  );

  return {
    getTeacher: getTeacherById,
    getTeachersOfSchool: _getTeachersOfSchool,
    addExternalTeacherToSchool: _addExternalTeacherToSchool,
  };
}
